/**
 * MODAL HOST — a stack of panels over a dimmed scrim.
 *
 * Measured from the reference captures (`docs/UI-INTERACTIONS.md`): a modal
 * opens in ONE frame at 30 fps — shop at frame n, full panel at n+1, under
 * 33 ms, with no fade and no slide. So this does not animate a panel in. The
 * scrim gets a 90 ms fade because a hard cut to black is the one part that
 * reads as a glitch; the PANEL itself appears.
 *
 * ── WHY A STACK ─────────────────────────────────────────────────────────────
 * The reference opens a confirm panel on top of a card's detail screen and
 * dismisses it back to that screen. A single-slot host cannot express that
 * without the caller tearing down and rebuilding the screen underneath, which
 * loses its scroll position and any in-flight state. So: push and pop.
 *
 * ── DISMISSAL ───────────────────────────────────────────────────────────────
 * Escape and a scrim tap both close the TOP panel only. A panel can refuse
 * (`dismissible: false`) for a confirm that must be answered. The close button
 * is the red X top-right of every panel that allows dismissal, per
 * `docs/UI-STYLE.md`.
 *
 * FOCUS. Opening moves focus into the panel and closing returns it to whatever
 * had it, because a modal that leaves focus behind it is invisible to a
 * keyboard and reads as broken to a screen reader.
 */
import { el, replaceChildren } from '../layout/dom';

import { playUiSound } from '../audio/ui-sounds';

import { PRESS_CSS, attachPress } from './press';

export interface ModalOptions {
  readonly title?: string;
  /** Escape and a scrim tap close it. Default true. */
  readonly dismissible?: boolean;
  /** Called after the panel leaves the stack, however it was closed. */
  readonly onClose?: () => void;
  /** Accessible label when there is no visible title. */
  readonly label?: string;
  /**
   * Extra classes for the panel box itself. Two modifiers ship (see MODAL_CSS):
   *
   *   `rga-modal__panel--fill`  a panel that takes a definite height and hands
   *                             it to its child instead of scrolling as one
   *                             box. Anything with a scrolling list ABOVE a
   *                             fixed row — the global chat — needs it.
   *   `rga-modal__panel--wide`  34rem instead of 26rem, and no body padding,
   *                             for a FORM that brings its own card — 480px of
   *                             content plus this panel's gutters.
   */
  readonly panelClass?: string;
}

interface Entry {
  readonly root: HTMLElement;
  readonly options: ModalOptions;
  readonly restoreFocus: Element | null;
  readonly teardown: (() => void)[];
}

export interface ModalHost {
  /** Push a panel. Returns a handle that closes exactly this one. */
  open(content: HTMLElement, options?: ModalOptions): { close: () => void };
  /** Close the top panel, if it allows it. */
  dismissTop(): void;
  readonly depth: number;
  destroy(): void;
}

export function createModalHost(container: HTMLElement): ModalHost {
  const doc = container.ownerDocument;
  const stack: Entry[] = [];

  const scrim = el('div', { class: 'rga-modal__scrim', 'data-rga': 'modal-scrim' });
  const layer = el('div', { class: 'rga-modal', 'data-rga': 'modal-layer' }, [scrim]);
  layer.hidden = true;
  container.append(layer);

  const top = (): Entry | undefined => stack[stack.length - 1];

  function close(entry: Entry): void {
    const i = stack.indexOf(entry);
    if (i < 0) return;
    stack.splice(i, 1);
    for (const t of entry.teardown) t();
    entry.root.remove();
    playUiSound('modal_close');
    if (stack.length === 0) {
      layer.hidden = true;
      layer.classList.remove('is-open');
    }
    const back = entry.restoreFocus;
    if (back instanceof HTMLElement && back.isConnected) back.focus();
    entry.options.onClose?.();
  }

  function dismissTop(): void {
    const t = top();
    if (t === undefined) return;
    if (t.options.dismissible === false) return;
    close(t);
  }

  /**
   * FOCUS STAYS IN THE TOP PANEL. aria-modal tells assistive technology the
   * page behind is inert, but a sighted keyboard user pressing Tab would still
   * walk out of the panel into the dimmed screen behind it and lose their
   * place. Tab and Shift+Tab therefore wrap inside the panel.
   */
  const FOCUSABLE =
    'button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),' +
    'textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const trapTab = (e: KeyboardEvent): void => {
    const t = top();
    if (t === undefined) return;
    const items = [...t.root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((n) => !n.hidden);
    if (items.length === 0) {
      e.preventDefault();
      t.root.focus?.();
      return;
    }
    const first = items[0] as HTMLElement;
    const last = items[items.length - 1] as HTMLElement;
    const active = doc.activeElement;
    if (e.shiftKey && (active === first || active === t.root || !t.root.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !t.root.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  };

  const onKey = (e: KeyboardEvent): void => {
    if (stack.length === 0) return;
    if (e.key === 'Tab') {
      trapTab(e);
      return;
    }
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    dismissTop();
  };
  doc.addEventListener('keydown', onKey, true);

  /**
   * THE SOFT KEYBOARD. `.rga-modal` is `position:fixed`, which pins it to the
   * LAYOUT viewport — and iOS Safari and Android Chrome both leave that at full
   * height when the keyboard opens (`interactive-widget=resizes-visual`, the
   * default, and what `index.html` asks for). So a centred panel stays centred
   * on a screen whose bottom half is now a keyboard, and the input row of
   * anything text-entry — the global chat above all — sits behind it, unreachable
   * and unscrollable because the panel is fixed.
   *
   * `visualViewport` is the only API that reports what is actually visible, so
   * the layer follows it: its height becomes the visible height and its top the
   * visible offset. `top` + `height` beat the `bottom` from `inset:0` (an
   * over-constrained absolutely positioned box drops `bottom`), so the grid
   * re-centres inside whatever the keyboard left behind. jsdom has no
   * `visualViewport`; the guard is what keeps this a no-op under test.
   */
  const viewport = doc.defaultView?.visualViewport ?? null;
  const syncViewport = (): void => {
    if (viewport === null) return;
    layer.style.height = `${viewport.height}px`;
    layer.style.top = `${viewport.offsetTop}px`;
  };
  if (viewport !== null) {
    viewport.addEventListener('resize', syncViewport);
    viewport.addEventListener('scroll', syncViewport);
    syncViewport();
  }

  const onScrim = (e: Event): void => {
    // Only a tap on the scrim ITSELF — a tap that started inside a panel and
    // released over the scrim must not close it.
    if (e.target !== scrim) return;
    dismissTop();
  };
  scrim.addEventListener('pointerup', onScrim);

  return {
    get depth() {
      return stack.length;
    },

    open(content, options = {}) {
      const dismissible = options.dismissible !== false;
      const teardown: (() => void)[] = [];

      const head: HTMLElement[] = [];
      if (options.title !== undefined) {
        head.push(el('h2', { class: 'rga-modal__title', text: options.title }));
      }
      if (dismissible) {
        const x = el('button', {
          type: 'button',
          class: 'rga-modal__close rga-press',
          'data-rga': 'modal-close',
          'data-sfx': 'none',
          'aria-label': 'Close',
        });
        teardown.push(attachPress(x, { onPress: () => close(entry) }));
        head.push(x);
      }

      const panel = el('div', {
        class:
          options.panelClass === undefined
            ? 'rga-modal__panel'
            : `rga-modal__panel ${options.panelClass}`,
        role: 'dialog',
        'aria-modal': 'true',
        ...(options.label === undefined ? {} : { 'aria-label': options.label }),
      }, [
        ...(head.length > 0 ? [el('div', { class: 'rga-modal__head' }, head)] : []),
        el('div', { class: 'rga-modal__body' }, [content]),
      ]);

      const entry: Entry = {
        root: panel,
        options,
        restoreFocus: doc.activeElement,
        teardown,
      };
      stack.push(entry);
      layer.append(panel);
      layer.hidden = false;
      layer.classList.add('is-open');

      // Focus the panel, not the close button: a screen reader should read the
      // dialog before it reads "Close".
      panel.setAttribute('tabindex', '-1');
      panel.focus?.();
      playUiSound('modal_open');

      return { close: () => close(entry) };
    },

    dismissTop,

    destroy() {
      doc.removeEventListener('keydown', onKey, true);
      scrim.removeEventListener('pointerup', onScrim);
      if (viewport !== null) {
        viewport.removeEventListener('resize', syncViewport);
        viewport.removeEventListener('scroll', syncViewport);
      }
      while (stack.length > 0) close(stack[stack.length - 1] as Entry);
      replaceChildren(layer);
      layer.remove();
    },
  };
}

export const MODAL_CSS = `
${PRESS_CSS}
/* The gutter is safe-area-aware: the layer is fixed to the viewport, so on a
   notched phone a centred panel otherwise sits under the cutout in landscape
   and over the home indicator in portrait. The panel's own width is then 100%
   of what the gutter LEFT rather than a viewport percentage, so the two can
   never add up to more than the screen and nothing is ever clipped. */
.rga-modal{position:fixed;inset:0;z-index:60;display:grid;place-items:center;
  padding:max(.5rem,env(safe-area-inset-top)) max(.75rem,env(safe-area-inset-right))
          max(.5rem,env(safe-area-inset-bottom)) max(.75rem,env(safe-area-inset-left))}
.rga-modal[hidden]{display:none!important}
/* The scrim is the LAYER's backdrop and must ignore that padding — it dims the
   whole screen, gutter included. */
.rga-modal__scrim{position:absolute;inset:0;background:rgba(8,14,32,.62);opacity:0;transition:opacity 90ms linear}
.rga-modal.is-open .rga-modal__scrim{opacity:1}
/* THE PANEL IS LEGIBLE IN ITS FIRST FRAME AND THEN SPRINGS. The reference
   captures (header) open a panel fully readable in one frame, so the entrance
   never fades: opacity is 1 from frame zero and the content is never hidden.
   What the AAA pass adds on top is a 240ms scale spring from 94% with one
   overshoot — the panel arrives as an object, not a cut — on the INDIVIDUAL
   scale property so it composes with anything a screen puts on transform.
   Reduced motion drops the spring and keeps the instant appearance. The rim
   is the 2px ladder rim, with a gold hairline inside it, and the top sheen. */
.rga-modal__panel{position:relative;z-index:1;max-width:min(100%,26rem);max-height:86dvh;
  overflow:auto;border-radius:var(--rga-radius-panel,14px);border:var(--rga-rim,2px) solid var(--color-border);
  background:var(--rga-face,none),var(--color-surface);color:var(--color-text);
  box-shadow:var(--rga-lift-1),var(--shadow-lift,0 18px 48px rgba(0,0,0,.6)),var(--rga-sheen),
    inset 0 0 0 1px color-mix(in srgb,var(--color-gold) 22%,transparent);outline:none;
  animation:rga-modal-spring 240ms cubic-bezier(.34,1.56,.64,1)}
@keyframes rga-modal-spring{0%{scale:.94}100%{scale:1}}
@media (prefers-reduced-motion:reduce){.rga-modal__panel{animation:none}}
/* ── A PANEL FOR A FORM, NOT A CONFIRM ──────────────────────────────────────
   26rem is the right width for a sentence and two buttons and the wrong one for
   a form: a registration-shaped form carries four fields and four consent rows,
   and in the default panel it was the same content the registration screen lays
   out in 480px, squeezed into 26rem less this panel's gutters. 34rem is 480px of
   content plus the body padding and the form's own gutter, so the two forms are
   the same box. Same specificity as the rule above (one class each), so this
   MUST stay below it — source order is what wins here. */
.rga-modal__panel--wide{width:min(100%,34rem);max-width:min(100%,34rem)}
/* The body gives its padding up because the card inside a wide panel brings its
   own gutter and its own padding. Keeping both stacks three paddings inside
   320px and the form ends up NARROWER than it was in the small panel, which is
   the opposite of the point. */
.rga-modal__panel--wide>.rga-modal__body{padding:0}
.rga-modal__head{display:flex;align-items:center;justify-content:space-between;gap:.75rem;padding:.85rem 1rem .35rem}
.rga-modal__title{margin:0;font-size:1.05rem;font-weight:700}
.rga-modal__body{padding:.35rem 1rem 1rem}
/* ── A PANEL WHOSE CONTENT OWNS THE SCROLL ──────────────────────────────────
   NOTE, AND IT IS NOT A STYLE POINT: this is a template literal, so a BACKTICK
   anywhere in these comments ends the string and the file stops compiling —
   with vitest reporting "no tests" rather than a syntax error. No code quotes
   below, on purpose.

   The default panel scrolls as ONE box. That is right for a confirm and wrong
   for anything with a list above a fixed row: on a phone the global chat's
   input row scrolled away with the conversation, because the scroller was the
   panel and not the list. The --fill modifier takes a definite height, hands it
   to the body, and lets the CHILD divide it — so the child can put a scroller
   in the middle and keep its own header and input row pinned.

   44rem caps it on a desktop (a 1400px-tall chat column is not a chat), and
   86dvh on a phone; dvh rather than vh so the value tracks the browser chrome
   collapsing on scroll. Same specificity as .rga-modal__panel (one class
   each), so this MUST stay below it — source order is what wins here. */
.rga-modal__panel--fill{display:flex;flex-direction:column;overflow:hidden;
  width:min(100%,34rem);max-width:min(100%,34rem);height:min(86dvh,44rem)}
.rga-modal__panel--fill>.rga-modal__body{flex:1 1 auto;min-height:0;padding:0;
  display:flex;flex-direction:column}
/* The close control: 44px target per §8, drawn as a cross. */
.rga-modal__close{position:relative;flex:0 0 auto;width:44px;height:44px;margin:-.35rem -.35rem 0 auto;
  border:0;border-radius:10px;background:transparent;cursor:pointer}
.rga-modal__close::before,.rga-modal__close::after{content:'';position:absolute;left:50%;top:50%;
  width:19px;height:3.5px;border-radius:2px;background:#ff5a4d;box-shadow:0 1px 0 rgba(0,0,0,.35)}
.rga-modal__close::before{transform:translate(-50%,-50%) rotate(45deg)}
.rga-modal__close::after{transform:translate(-50%,-50%) rotate(-45deg)}
`;
